import { Component } from '@angular/core';
import { GridReadyEvent } from '@ag-grid-community/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AgFns, GridOptionsExt } from '@/shared/ag-fns';
import { DialogBaseComponent, DialogParams } from '@/shared/dialog-base.component';
import { DomainService } from '@/shared/domain.service';

export interface ExcludedAccountsDialogParams extends DialogParams {
  /** The accounts this dashboard view already puts back in. */
  includedAccountIds: number[];
}

interface ExcludedAccountRow {
  accountId: number;
  accountName: string;
  _isIncludeChecked: boolean;
}

/** Lists the accounts set to Exclude from Dashboard Analytics. Closes with the ids to include
 *  in this dashboard view only, or null on Cancel. */
@Component({
  templateUrl: './excluded-accounts-dialog.component.html',
  standalone: false,
})
export class ExcludedAccountsDialogComponent extends DialogBaseComponent<ExcludedAccountsDialogParams> {
  gridOptions: GridOptionsExt;
  rows: ExcludedAccountRow[] = [];

  constructor(
    config: DynamicDialogConfig<ExcludedAccountsDialogParams>,
    dialogRef: DynamicDialogRef,
    domainService: DomainService,
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, { header: 'Excluded Accounts' });
    this.prepare();
  }

  private async prepare() {
    const accounts = await this.dbQueryService.getAnalyticsExcludedAccounts();
    const included = new Set(this.params.includedAccountIds);
    this.rows = accounts.map((a) => ({
      accountId: a.id,
      accountName: a.accountName,
      _isIncludeChecked: included.has(a.id),
    }));
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady.bind(this),
      getRowId: (p) => String(p.data.accountId),
      cellSelection: false,
      pagination: false,
    });
    this.isPageReady = true;
  }

  onGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: 'Account', field: 'accountName', ...AgFns.flexWidth(200) },
      {
        ...AgFns.createCheckboxProps('Include?', '_isIncludeChecked', 'includeCheckbox'),
        valueGetter: (p: any) => !!p.data?._isIncludeChecked,
        valueSetter: (p: any) => {
          if (!p.data) return false;
          p.data._isIncludeChecked = !!p.newValue;
          return true;
        },
        editable: true,
        sortable: false,
        filter: false,
        ...AgFns.fixWidth(100),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: 'accountName', sort: 'asc' }]);
  }

  ok() {
    this.gridOptions?.apiX?.stopEditing();
    this.dialogRef.close(this.rows.filter((r) => r._isIncludeChecked).map((r) => r.accountId));
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
